import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { AlertTriangleIcon, BellIcon, MessageSquareTextIcon } from 'lucide-react';
import { useAdminData } from '../../../contexts/AdminDataContext';
import { useDismiss } from '../../../hooks/useDismiss';
import { isOverdue } from '../../../utils/invoice';
import { relativeAgo } from '../../../utils/adminDates';
const ease = [0.23, 1, 0.32, 1] as const;
interface Notice {
  id: string;
  title: string;
  detail: string;
  to: string;
  tone: 'new' | 'overdue';
}
export function NotificationsMenu() {
  const {
    enquiries,
    invoices
  } = useAdminData();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const close = useCallback(() => setOpen(false), []);
  useDismiss(wrapRef, open, close);
  const notices = useMemo<Notice[]>(() => {
    const fresh = enquiries.filter(item => item.status === 'Ny henvendelse').sort((a, b) => b.createdAt.localeCompare(a.createdAt)).map<Notice>(item => ({
      id: `e-${item.id}`,
      title: `Ny henvendelse fra ${item.name}`,
      detail: relativeAgo(item.createdAt),
      to: '/admin/henvendelser',
      tone: 'new'
    }));
    const overdue = invoices.filter(isOverdue).map<Notice>(invoice => ({
      id: `f-${invoice.id}`,
      title: `Faktura ${invoice.invoiceNumber} er forfalden`,
      detail: invoice.customerName,
      to: `/admin/fakturaer/${invoice.id}`,
      tone: 'overdue'
    }));
    return [...fresh, ...overdue];
  }, [enquiries, invoices]);
  return <div ref={wrapRef} className="relative" data-id="element-583" data-mp="iemrvq0qbaoo">
      <button type="button" onClick={() => setOpen(value => !value)} aria-expanded={open} aria-label={notices.length ? `Notifikationer (${notices.length})` : 'Notifikationer'} className="relative inline-flex h-10 w-10 items-center justify-center rounded-xl text-ink-soft transition-colors duration-150 ease-smooth hover:bg-white hover:text-ink" data-id="element-584" data-mp="n6xrssvgywh8">
        <BellIcon className="h-5 w-5" strokeWidth={1.75} aria-hidden="true" data-id="element-585" data-mp="3etpyvfm3tyo0" />
        {notices.length > 0 && <span className="absolute right-2 top-2 h-2 w-2 rounded-full bg-danger ring-2 ring-canvas" aria-hidden="true" data-id="element-586" data-mp="3skv1mqkrr72k" />}
      </button>

      <AnimatePresence data-id="element-587" data-mp="3nml7mjas6cij">
        {open && <motion.div initial={{
        opacity: 0,
        y: -4,
        scale: 0.98
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: -4,
        scale: 0.98
      }} transition={{
        duration: 0.16,
        ease
      }} className="absolute right-0 top-[calc(100%+8px)] z-50 w-[min(22rem,calc(100vw-2rem))] origin-top-right overflow-hidden rounded-xl border border-line bg-white shadow-lift" data-id="element-588" data-mp="2zw7d8m4q41ks">
            <p className="border-b border-line px-4 py-3 text-sm font-semibold text-ink" data-id="element-589" data-mp="p3cyuxjusq90">Notifikationer</p>
            {notices.length === 0 ? <p className="px-4 py-6 text-sm text-ink-muted" data-id="element-590" data-mp="k7p7cbhwfl0c">Du er helt opdateret.</p> : <ul className="max-h-80 divide-y divide-line overflow-y-auto" data-id="element-591" data-mp="225anolnex958">
                {notices.map(notice => {
            const Icon = notice.tone === 'new' ? MessageSquareTextIcon : AlertTriangleIcon;
            return <li key={notice.id} data-id="element-592" data-mp="1y2e5ux5p5h2c">
                      <Link to={notice.to} onClick={close} className="flex items-start gap-3 px-4 py-3 transition-colors duration-100 hover:bg-canvas-deep" data-id="element-593" data-mp="1y37czz8ki8l0">
                        <Icon className={`mt-0.5 h-4 w-4 shrink-0 ${notice.tone === 'new' ? 'text-accent' : 'text-danger'}`} strokeWidth={1.75} aria-hidden="true" data-id="element-594" data-mp="2c02qq4zrko7e" />
                        <span className="min-w-0" data-id="element-595" data-mp="39g9jg1gydhrg">
                          <span className="block text-sm text-ink" data-id="element-596" data-mp="39i2pbejqrxye">{notice.title}</span>
                          {notice.detail && <span className="block text-xs text-ink-muted" data-id="element-597" data-mp="1kw03sbc6x6q2">{notice.detail}</span>}
                        </span>
                      </Link>
                    </li>;
          })}
              </ul>}
          </motion.div>}
      </AnimatePresence>
    </div>;
}